import { useCallback, useEffect, useRef, useState } from 'react';

type Layout = 'grid' | 'list';

/**
 * Manages a grid/list layout preference, persisted to localStorage.
 * @param storageKey  - localStorage key to persist the user's selection
 * @param defaultLayout - defaults to 'grid' if no stored value found
 */
export function useLayoutSelector(
  storageKey: string,
  defaultLayout: Layout = 'grid',
): [Layout, (layout: Layout) => void] {
  const initialized = useRef<boolean>(false);

  const [layout, setLayout] = useState<Layout>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored === 'grid' || stored === 'list' ? stored : defaultLayout;
    } catch {
      return defaultLayout;
    }
  });

  const handleSetLayout = useCallback((value: Layout) => {
    setLayout(value);
  }, []);

  useEffect(() => {
    if (initialized.current) {
      try {
        window.localStorage.setItem(storageKey, layout);
      } catch {
        //
      }
    } else {
      initialized.current = true;
    }
  }, [storageKey, layout]);

  return [layout, handleSetLayout];
}
